// 149-cw-r133 (2026-08-31) — Export helper untuk Laporan Overhead Kantor.
// exportOverheadToExcel → .xlsx via SheetJS (multi-sheet: Summary / By Month / By Category / Line Items).
// printOverheadToPDF    → print-friendly HTML window (native browser print → Save as PDF).

import * as XLSX from "xlsx";
import { fmtDate, formatIDR } from "@/lib/constants";

function _fmtMonth(ym) {
  if (!ym) return "-";
  const [y, m] = ym.split("-");
  const months = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
  return `${months[Number(m) - 1] || m} ${y}`;
}

export function exportOverheadToExcel(report, filename = "laporan-overhead-kantor.xlsx") {
  if (!report) return;
  const wb = XLSX.utils.book_new();

  // Sheet 1 — Ringkasan
  const s = report.summary || {};
  const summarySheet = [
    ["LAPORAN OVERHEAD KANTOR"],
    [`Periode: ${s.date_from || "-"} s/d ${s.date_to || "-"}`],
    [`Digenerate: ${report.generated_at ? fmtDate(report.generated_at) : "-"}`],
    [],
    ["Total Pengeluaran", s.total || 0],
    ["Jumlah Transaksi", s.count || 0],
    ["Rata-rata per Bulan", s.avg_monthly || 0],
    ["Jumlah Bulan", s.period_months || 0],
    ["Kategori Terbesar", s.top_category || "-"],
    ["Nominal Kategori Terbesar", s.top_amount || 0],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summarySheet), "Ringkasan");

  // Sheet 2 — Per Kategori
  const catRows = [["Kategori", "Total", "Jumlah Transaksi", "% dari Total"]];
  for (const c of (report.by_category || [])) {
    catRows.push([c.category, c.total, c.count, `${c.pct}%`]);
  }
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(catRows), "Per Kategori");

  // Sheet 3 — Per Bulan
  const monthRows = [["Bulan", "Total", "Jumlah Transaksi"]];
  for (const m of (report.by_month || [])) {
    monthRows.push([_fmtMonth(m.ym), m.total, m.count]);
  }
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(monthRows), "Per Bulan");

  // Sheet 4 — Per Project
  const projRows = [["Proyek/Kantor", "Total", "Jumlah Transaksi"]];
  for (const p of (report.by_project || [])) {
    projRows.push([p.project_name, p.total, p.count]);
  }
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(projRows), "Per Proyek");

  // Sheet 5 — Rincian
  const itemRows = [["Tanggal", "Proyek/Kantor", "Kategori", "Nominal", "Referensi", "Bank", "Catatan", "Oleh", "Sumber"]];
  for (const r of (report.line_items || [])) {
    itemRows.push([r.date, r.project_name, r.category, r.amount, r.ref, r.bank_name, r.note, r.by, r.source]);
  }
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(itemRows), "Rincian");

  XLSX.writeFile(wb, filename);
}

export function printOverheadToPDF(report) {
  if (!report) return;
  const s = report.summary || {};
  const now = new Date().toLocaleString("id-ID");

  const w = window.open("", "_blank", "width=1024,height=768");
  if (!w) return;

  const rowsCat = (report.by_category || [])
    .map((c) => `<tr><td>${c.category}</td><td style="text-align:right">${formatIDR(c.total)}</td><td style="text-align:right">${c.count}</td><td style="text-align:right">${c.pct}%</td></tr>`)
    .join("");
  const rowsMonth = (report.by_month || [])
    .map((m) => `<tr><td>${_fmtMonth(m.ym)}</td><td style="text-align:right">${formatIDR(m.total)}</td><td style="text-align:right">${m.count}</td></tr>`)
    .join("");
  const rowsItems = (report.line_items || [])
    .map((r, i) => `<tr><td style="text-align:center">${i + 1}</td><td>${r.date || "-"}</td><td>${r.project_name || "-"}</td><td>${r.category}</td><td style="text-align:right">${formatIDR(r.amount)}</td><td>${r.ref || "-"}</td><td>${r.note || "-"}</td></tr>`)
    .join("");

  w.document.write(`<!doctype html><html lang="id"><head><meta charset="utf-8"><title>Laporan Overhead Kantor</title>
<style>
  body { font-family: 'Helvetica Neue', Arial, sans-serif; padding: 24px; color: #0f172a; }
  h1 { font-size: 20px; color: #0B1D3A; margin: 0 0 4px; letter-spacing: 0.02em; }
  .meta { color: #64748b; font-size: 11px; margin-bottom: 16px; }
  .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 20px; }
  .kpi { border: 1px solid #e2e8f0; border-left: 4px solid #0B1D3A; padding: 8px 10px; }
  .kpi .label { font-size: 9px; text-transform: uppercase; letter-spacing: 0.12em; color: #64748b; }
  .kpi .value { font-size: 15px; font-weight: 700; color: #0B1D3A; margin-top: 2px; }
  h2 { font-size: 13px; color: #0B1D3A; text-transform: uppercase; letter-spacing: 0.08em; border-bottom: 2px solid #0B1D3A; padding-bottom: 4px; margin: 20px 0 8px; }
  table { width: 100%; border-collapse: collapse; font-size: 10px; }
  th { background: #0B1D3A; color: white; padding: 6px 8px; text-align: left; text-transform: uppercase; letter-spacing: 0.06em; font-size: 9px; }
  td { padding: 5px 8px; border-bottom: 1px solid #e2e8f0; }
  tr:nth-child(even) td { background: #f8fafc; }
  @media print { body { padding: 12px; } .no-print { display: none; } }
  .btn { background: #0B1D3A; color: white; border: none; padding: 6px 14px; font-size: 11px; cursor: pointer; text-transform: uppercase; letter-spacing: 0.1em; }
</style></head><body>
<div class="no-print" style="text-align:right; margin-bottom:12px;"><button class="btn" onclick="window.print()">Cetak / Save PDF</button></div>
<h1>Laporan Overhead Kantor</h1>
<div class="meta">Periode: ${s.date_from || "-"} s/d ${s.date_to || "-"} · Digenerate ${now}</div>
<div class="kpi-grid">
  <div class="kpi"><div class="label">Total Pengeluaran</div><div class="value">${formatIDR(s.total)}</div></div>
  <div class="kpi"><div class="label">Jumlah Transaksi</div><div class="value">${s.count || 0}</div></div>
  <div class="kpi"><div class="label">Rata-rata / Bulan</div><div class="value">${formatIDR(s.avg_monthly)}</div></div>
  <div class="kpi"><div class="label">Kategori Terbesar</div><div class="value" style="font-size:11px;">${s.top_category || "-"}</div></div>
</div>
<h2>Rincian per Kategori</h2>
<table><thead><tr><th>Kategori</th><th style="text-align:right">Total</th><th style="text-align:right">Trx</th><th style="text-align:right">%</th></tr></thead><tbody>${rowsCat || '<tr><td colspan="4" style="text-align:center;color:#94a3b8;font-style:italic">Tidak ada data</td></tr>'}</tbody></table>
<h2>Rincian per Bulan</h2>
<table><thead><tr><th>Bulan</th><th style="text-align:right">Total</th><th style="text-align:right">Trx</th></tr></thead><tbody>${rowsMonth || '<tr><td colspan="3" style="text-align:center;color:#94a3b8;font-style:italic">Tidak ada data</td></tr>'}</tbody></table>
<h2>Daftar Transaksi</h2>
<table><thead><tr><th style="text-align:center">No.</th><th>Tanggal</th><th>Proyek/Kantor</th><th>Kategori</th><th style="text-align:right">Nominal</th><th>Referensi</th><th>Catatan</th></tr></thead><tbody>${rowsItems || '<tr><td colspan="7" style="text-align:center;color:#94a3b8;font-style:italic">Tidak ada transaksi</td></tr>'}</tbody></table>
</body></html>`);
  w.document.close();
  setTimeout(() => { try { w.focus(); } catch { /* noop */ } }, 300);
}
